import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, Field, TextInput, TextArea, SelectInput, EmptyState } from "../components/Primitives";
import { useAuth } from "../lib/auth";

const TABS = [
  { key: "overview", label: "Overview", icon: "LayoutDashboard" },
  { key: "credentials", label: "Credentials", icon: "KeyRound" },
  { key: "settings", label: "Settings & Scope", icon: "Settings2" },
  { key: "mapping", label: "Field Mapping", icon: "GitCompare" },
  { key: "logs", label: "Sync Logs", icon: "ScrollText" },
  { key: "errors", label: "Error Logs", icon: "AlertOctagon" },
];

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2" style={{ borderBottom: "1px solid var(--border)" }}>
      <div className="text-[11px] uppercase tracking-wider muted col-span-1 font-semibold">{label}</div>
      <div className="col-span-2 text-[13px] ink break-words">{value ?? "—"}</div>
    </div>
  );
}

export default function ConnectorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "org_admin"].includes(user?.role);

  const [inst, setInst] = useState(null);
  const [tab, setTab] = useState("overview");
  const [logs, setLogs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [sample, setSample] = useState({});
  const [sampleEntity, setSampleEntity] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // form states
  const [cred, setCred] = useState({});
  const [settings, setSettings] = useState({ sync_frequency: "manual", enabled: true });
  const [scope, setScope] = useState([]);
  const [mapping, setMapping] = useState({});
  const [activeMapEntity, setActiveMapEntity] = useState("");

  const load = async () => {
    try {
      const [d, l, e] = await Promise.all([
        api.get(`/connectors/${id}`),
        api.get(`/connectors/${id}/logs?kind=sync&limit=25`),
        api.get(`/connectors/${id}/logs?kind=error&limit=25`),
      ]);
      setInst(d.data);
      setLogs(l.data.items || []);
      setErrors(e.data.items || []);
      const cfg = d.data.config || {};
      const clean = {};
      Object.keys(cfg).forEach((k) => { clean[k] = cfg[k] === "••••••••" ? "" : cfg[k]; });
      setCred(clean);
      setSettings({ sync_frequency: d.data.sync_frequency || "manual", enabled: d.data.enabled });
      setScope(d.data.sync_scope?.entities || []);
      setMapping(d.data.field_mappings || {});
      const entities = d.data.definition?.sync_entities || [];
      if (entities.length > 0) {
        setActiveMapEntity(entities[0].key);
        setSampleEntity(entities[0].key);
      }
    } catch (e) {
      toast.error("Failed to load connector");
    }
  };

  useEffect(() => { load();   }, [id]);

  const loadSample = async () => {
    try {
      const { data } = await api.get(`/connectors/${id}/records?entity=${encodeURIComponent(sampleEntity)}&limit=5`);
      setSample({ [sampleEntity]: data.items });
    } catch { /* silent */ }
  };
  useEffect(() => { if (sampleEntity) loadSample();   }, [sampleEntity]);

  const doTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/connectors/${id}/test`);
      if (data.ok) toast.success(`Connected · ${data.message} · ${data.latency_ms || 0} ms`);
      else toast.error(data.message || "Test failed");
      await load();
    } catch { toast.error("Test failed"); }
    finally { setTesting(false); }
  };

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/connectors/${id}/sync`);
      if (data.ok) toast.success(`Pulled ${data.total_records} records from ${data.entities.length} entities`);
      else toast.error(`Sync completed with ${data.errors?.length || 0} errors (${data.total_records} records)`);
      await load();
      if (sampleEntity) loadSample();
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const startOAuth = async () => {
    try {
      const { data } = await api.post(`/connectors/${id}/oauth/start`);
      window.open(data.authorize_url, "connector_oauth", "width=560,height=720");
      toast.info("Complete the authorization flow in the popup, then refresh this page.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start OAuth");
    }
  };

  const saveCredentials = async () => {
    setSaving(true);
    try {
      await api.put(`/connectors/${id}`, { config: cred });
      toast.success("Credentials updated");
      await load();
    } catch { toast.error("Failed to save credentials"); }
    finally { setSaving(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put(`/connectors/${id}`, {
        sync_frequency: settings.sync_frequency,
        enabled: settings.enabled,
        sync_scope: { entities: scope },
      });
      toast.success("Settings saved");
      await load();
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const saveMapping = async () => {
    setSaving(true);
    try {
      await api.put(`/connectors/${id}/field-mappings`, { field_mappings: mapping });
      toast.success("Field mapping saved");
      await load();
    } catch { toast.error("Failed to save mapping"); }
    finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this connector? All stored credentials will be deleted.")) return;
    try {
      await api.delete(`/connectors/${id}`);
      toast.success("Revoked");
      nav("/app/connectors");
    } catch { toast.error("Failed to revoke"); }
  };

  const defn = inst?.definition;
  const entities = defn?.sync_entities || [];
  const currentMapping = mapping[activeMapEntity] || defn?.default_mappings || {};

  const platformFields = useMemo(() => [
    "external_id", "email", "name", "first_name", "last_name",
    "phone", "title", "status", "url", "file_type", "resource_type",
    "severity", "label",
  ], []);

  if (!inst) return <Loader />;

  return (
    <div>
      <div className="mb-4">
        <Link to="/app/connectors" className="text-[12px] muted hover:ink inline-flex items-center gap-1" data-testid="back-to-connectors">
          <Icons.ArrowLeft className="w-3.5 h-3.5" /> All connectors
        </Link>
      </div>

      <PageHeader
        title={inst.name}
        kicker={`${defn?.category} · ${defn?.vendor}`}
        description={defn?.description}
        testId="connector-detail-header"
        actions={
          <>
            {isAdmin && (
              <>
                <Btn variant="secondary" onClick={doTest} disabled={testing} data-testid="detail-test-btn">
                  <Icons.PlugZap className="w-3.5 h-3.5" /> {testing ? "Testing…" : "Test connection"}
                </Btn>
                <Btn onClick={doSync} disabled={syncing} data-testid="detail-sync-btn">
                  <Icons.RotateCw className="w-3.5 h-3.5" /> {syncing ? "Syncing…" : "Sync now"}
                </Btn>
              </>
            )}
            {isAdmin && defn?.has_oauth && (
              <Btn variant="secondary" onClick={startOAuth} data-testid="detail-oauth-btn">
                <Icons.LogIn className="w-3.5 h-3.5" /> Authorize
              </Btn>
            )}
            {isAdmin && (
              <Btn variant="danger" onClick={revoke} data-testid="detail-revoke-btn">
                <Icons.Trash2 className="w-3.5 h-3.5" /> Revoke
              </Btn>
            )}
          </>
        }
      />

      {/* Tabs */}
      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 overflow-x-auto"
        style={{ border: "1px solid var(--border)", width: "fit-content" }}>
        {TABS.map((t) => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button
              key={t.key}
              data-testid={`detail-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 whitespace-nowrap transition-all"
              style={{
                background: tab === t.key ? "var(--primary-soft)" : "transparent",
                color: tab === t.key ? "var(--primary)" : "var(--muted)",
              }}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
              {t.key === "errors" && errors.length > 0 && (
                <span className="ml-1 px-1.5 py-[1px] rounded-full text-[9px] font-bold"
                  style={{ background: "var(--danger)", color: "white" }}>
                  {errors.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Status</div>
            <Row label="Health" value={inst.health || "unknown"} />
            <Row label="Enabled" value={inst.enabled ? "Yes" : "No"} />
            <Row label="Auth type" value={defn?.auth_type} />
            <Row label="OAuth authorized" value={defn?.has_oauth ? (inst.oauth_authorized ? "Yes ✅" : "No — click Authorize") : "N/A"} />
            <Row label="Last test" value={inst.last_test_at ? new Date(inst.last_test_at).toLocaleString() : "never"} />
            <Row label="Last test result" value={inst.last_test_result?.message} />
          </div>

          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Sync</div>
            <Row label="Frequency" value={inst.sync_frequency} />
            <Row label="Last sync" value={inst.last_sync_at ? new Date(inst.last_sync_at).toLocaleString() : "never"} />
            <Row label="Last result" value={inst.last_sync_status} />
            <Row label="Records pulled" value={inst.last_sync_records || 0} />
            <Row label="Next scheduled run" value={inst.next_run_at ? new Date(inst.next_run_at).toLocaleString() : "manual only"} />
            <Row label="Scoped entities" value={(inst.sync_scope?.entities || []).join(", ") || "—"} />
          </div>
        </div>
      )}

      {tab === "credentials" && (
        <div className="surface rounded-xl p-5 max-w-2xl" style={{ border: "1px solid var(--border)" }}>
          <div className="text-[13px] muted mb-4 flex items-center gap-2">
            <Icons.Lock className="w-3.5 h-3.5" />
            Secrets are encrypted at rest with Fernet (AES-128) and never displayed after save.
          </div>
          <div className="grid gap-4">
            {(defn?.config_fields || []).map((f) => (
              <Field key={f.key} label={f.label} required={f.required}>
                {f.type === "textarea" ? (
                  <TextArea
                    data-testid={`cred-${f.key}`}
                    value={cred[f.key] || ""}
                    placeholder={f.secret ? "•••••• (leave blank to keep existing)" : f.placeholder}
                    onChange={(e) => setCred({ ...cred, [f.key]: e.target.value })}
                  />
                ) : f.type === "select" ? (
                  <SelectInput
                    data-testid={`cred-${f.key}`}
                    value={cred[f.key] || ""}
                    onChange={(e) => setCred({ ...cred, [f.key]: e.target.value })}
                  >
                    <option value="">-- Select --</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </SelectInput>
                ) : f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid={`cred-${f.key}`} type="checkbox"
                      checked={!!cred[f.key]}
                      onChange={(e) => setCred({ ...cred, [f.key]: e.target.checked })} />
                    <span className="muted">{f.label}</span>
                  </label>
                ) : (
                  <TextInput
                    data-testid={`cred-${f.key}`}
                    type={f.secret ? "password" : (f.type === "number" ? "number" : "text")}
                    placeholder={f.secret ? "•••••• (leave blank to keep existing)" : f.placeholder}
                    value={cred[f.key] ?? ""}
                    onChange={(e) => setCred({ ...cred, [f.key]: e.target.value })}
                  />
                )}
                {f.help && <div className="text-[11px] muted mt-1">{f.help}</div>}
              </Field>
            ))}
          </div>
          {isAdmin && (
            <div className="mt-5 flex gap-2">
              <Btn onClick={saveCredentials} disabled={saving} data-testid="save-credentials-btn">
                {saving ? "Saving…" : "Save credentials"}
              </Btn>
              <Btn variant="secondary" onClick={doTest} data-testid="cred-test-btn">
                <Icons.PlugZap className="w-3.5 h-3.5" /> Test now
              </Btn>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div className="surface rounded-xl p-5 max-w-2xl" style={{ border: "1px solid var(--border)" }}>
          <div className="grid gap-4">
            <Field label="Sync frequency">
              <SelectInput
                data-testid="settings-frequency"
                value={settings.sync_frequency}
                onChange={(e) => setSettings({ ...settings, sync_frequency: e.target.value })}
              >
                <option value="manual">Manual only</option>
                <option value="every_15_min">Every 15 minutes</option>
                <option value="hourly">Hourly</option>
                <option value="every_6_hours">Every 6 hours</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </SelectInput>
            </Field>
            <Field label="Sync scope (entities)">
              <div className="grid grid-cols-2 gap-2">
                {entities.map((e) => (
                  <label key={e.key} className="flex items-center gap-2 text-sm surface p-2 rounded-md"
                    style={{ border: "1px solid var(--border)" }}>
                    <input
                      data-testid={`scope-${e.key}`}
                      type="checkbox"
                      checked={scope.includes(e.key)}
                      onChange={(ev) => {
                        setScope(ev.target.checked
                          ? [...scope, e.key]
                          : scope.filter((k) => k !== e.key));
                      }}
                    />
                    <span className="ink capitalize">{e.key.replace(/_/g, " ")}</span>
                  </label>
                ))}
                {entities.length === 0 && (
                  <div className="text-[12px] muted">This connector has no data-pull entities configured.</div>
                )}
              </div>
            </Field>
            <Field label="Enabled">
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="settings-enabled"
                  type="checkbox"
                  checked={!!settings.enabled}
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                />
                <span className="ink">Connector is active and will run scheduled syncs</span>
              </label>
            </Field>
          </div>
          {isAdmin && (
            <div className="mt-5">
              <Btn onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
                {saving ? "Saving…" : "Save settings"}
              </Btn>
            </div>
          )}
        </div>
      )}

      {tab === "mapping" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold ink">Field Mapping</div>
              <SelectInput
                data-testid="mapping-entity"
                value={activeMapEntity}
                onChange={(e) => setActiveMapEntity(e.target.value)}
                style={{ maxWidth: 200 }}
              >
                {entities.map((e) => <option key={e.key} value={e.key}>{e.key}</option>)}
              </SelectInput>
            </div>
            <div className="text-[12px] muted mb-3">
              Source field paths (dot notation, e.g. <code>profile.email</code>) → platform entity field.
              Defaults from the connector definition are pre-filled; adjust to match your data model.
            </div>
            <div className="grid gap-2">
              {Object.entries(currentMapping).map(([src, dst], idx) => (
                <div key={`${src}-${idx}`} className="flex items-center gap-2">
                  <TextInput
                    data-testid={`mapping-src-${idx}`}
                    value={src}
                    onChange={(e) => {
                      const next = { ...currentMapping };
                      delete next[src];
                      next[e.target.value] = dst;
                      setMapping({ ...mapping, [activeMapEntity]: next });
                    }}
                    placeholder="source.field.path"
                  />
                  <Icons.ArrowRight className="w-4 h-4 muted-2 shrink-0" />
                  <SelectInput
                    data-testid={`mapping-dst-${idx}`}
                    value={dst}
                    onChange={(e) => {
                      const next = { ...currentMapping, [src]: e.target.value };
                      setMapping({ ...mapping, [activeMapEntity]: next });
                    }}
                  >
                    {platformFields.map((f) => <option key={f} value={f}>{f}</option>)}
                    <option value={dst}>{dst}</option>
                  </SelectInput>
                  <button
                    data-testid={`mapping-remove-${idx}`}
                    onClick={() => {
                      const next = { ...currentMapping };
                      delete next[src];
                      setMapping({ ...mapping, [activeMapEntity]: next });
                    }}
                    className="p-1.5 rounded-md muted hover:danger transition-all"
                  >
                    <Icons.X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Btn variant="secondary" size="sm" onClick={() => {
                setMapping({ ...mapping, [activeMapEntity]: { ...currentMapping, "": "external_id" } });
              }} data-testid="mapping-add-btn">
                <Icons.Plus className="w-3.5 h-3.5" /> Add mapping
              </Btn>
            </div>
            {isAdmin && (
              <div className="mt-4">
                <Btn onClick={saveMapping} disabled={saving} data-testid="save-mapping-btn">
                  {saving ? "Saving…" : "Save mapping"}
                </Btn>
              </div>
            )}
          </div>

          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold ink">Sample record preview</div>
              <SelectInput
                data-testid="sample-entity"
                value={sampleEntity}
                onChange={(e) => setSampleEntity(e.target.value)}
                style={{ maxWidth: 200 }}
              >
                {entities.map((e) => <option key={e.key} value={e.key}>{e.key}</option>)}
              </SelectInput>
            </div>
            <div className="text-[11px] muted mb-2">
              Live sample from the most recent sync run. Run <b>Sync now</b> to refresh.
            </div>
            {(sample[sampleEntity] || []).length === 0 ? (
              <EmptyState title="No sample records yet" hint="Run 'Sync now' to fetch data from the source." />
            ) : (
              <pre className="text-[11px] leading-relaxed p-3 rounded-md overflow-auto max-h-[420px] mono"
                style={{ background: "var(--bg-2)", color: "var(--ink)" }}
                data-testid="sample-json">
                {JSON.stringify(sample[sampleEntity].slice(0, 3), null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {tab === "logs" && (
        <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg-2)" }}>
              <tr>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">Started</th>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">Trigger</th>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">Result</th>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">Records</th>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">Entities</th>
              </tr>
            </thead>
            <tbody data-testid="logs-tbody">
              {logs.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center muted">No sync runs yet</td></tr>
              ) : logs.map((l) => (
                <tr key={l.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td className="px-4 py-2 ink text-[12px]">{new Date(l.started_at).toLocaleString()}</td>
                  <td className="px-4 py-2 muted text-[12px] capitalize">{l.trigger}</td>
                  <td className="px-4 py-2 text-[12px]" style={{ color: l.ok ? "var(--success)" : "var(--danger)" }}>
                    {l.ok ? "success" : "error"}
                  </td>
                  <td className="px-4 py-2 ink text-[12px]">{l.total_records}</td>
                  <td className="px-4 py-2 muted text-[12px]">
                    {(l.entities || []).map((e) => `${e.key}:${e.count}${e.error ? "❗" : ""}`).join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "errors" && (
        <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg-2)" }}>
              <tr>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">When</th>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">Entity</th>
                <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">Message</th>
              </tr>
            </thead>
            <tbody data-testid="errors-tbody">
              {errors.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center muted">No errors 🎉</td></tr>
              ) : errors.map((e) => (
                <tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td className="px-4 py-2 ink text-[12px]">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 ink text-[12px]">{e.entity}</td>
                  <td className="px-4 py-2 danger text-[12px]" style={{ color: "var(--danger)" }}>{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
